import React, { useState, useEffect, Fragment } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, Link as RouterLink, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

// material-ui
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import PolicyIcon from '@material-ui/icons/Policy';
import Grid from '@material-ui/core/Grid';
import AcceptIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import BackIcon from '@material-ui/icons/KeyboardArrowLeft';
import Skeleton from '@material-ui/lab/Skeleton';
import Link from '@material-ui/core/Link';
import MenuList from '@material-ui/core/MenuList';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import MembershipGoodIcon from '@material-ui/icons/VerifiedUser';
import MembershipBadIcon from '@material-ui/icons/Error';


// local
import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';
import FormatPhoneNumber from 'app/utils/FormatPhoneNumber';
import DetailSkeleton from 'app/components/loaders/DetailSkeleton';
import { containerFlex, containerFlexFixed, containerFlexGrow } from 'app/styles/new-layout';
import RedButton from 'app/components/buttons/RedButton';


const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3),
    },
    content: {
        ...containerFlexGrow(theme),
        position: 'relative',
        paddingLeft: theme.spacing(3),

        [theme.breakpoints.down('md')]: {
            paddingLeft: '0px',
        }
    },
    nav: {
        ...containerFlexFixed(theme),
    },
    paperMembership: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3)
    },
    messagePaper: {
        padding: theme.spacing(3)
    },
    messageGrid: {
        [theme.breakpoints.down('sm')]: {
            direction: 'column'
        }
    },
    link: {
        textDecoration: 'none'
    },
    emptyCon: {
        padding: theme.spacing(3),
        textAlign: 'center'
    },
    section: {
        marginTop: theme.spacing(3)
    },
    sectionCenter: {
        textAlign: 'center'
    },
    alert: {
        marginBottom: theme.spacing(3),
        color: 'red'
    },
    menuItem: {
        borderRadius: theme.shape.borderRadius    
    },
    membershipIconContainer: {
        textAlign: 'center',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    textSuccess: {
        color: theme.palette.success.main
    },
    membershipGoodIcon: {
        width: '10%',
        height: "auto",
        display: 'inline-block',
        color: theme.palette.success.main,

        [theme.breakpoints.down('md')]: {
            width: '20%'
        }
    },
    membershipBadIcon: {
        width: '10%',
        height: "auto",
        color: theme.palette.error.main,
        display: 'inline-block',

        [theme.breakpoints.down('md')]: {
            width: '20%'
        }
    },
    skeleton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
    }
}));

export default function ResignationRequestDetails(props) {

    const initialMembership = {
                                is_membership_paid: null,
                                in_good_standing: true,
                                renews_on: null,
                                renews_in: null,
                                due_on: null,
                                invoice: null,
                                club_verified_at: null,
                                level_id: null
                                }
    
    const [ request, setRequest ] = useState(null);
    const [ requestLoading, setRequestLoading ] = useState(false);
    const [ user, setUser ] = useState(null);
    const [ verifyApproveResignationOpen , setVerifyApproveResignationOpen ] = useState(false);
    const [ membership, setMembership ] = useState(null);

    
    const { app } = useApp();
    const api = useApi();
    const classes = useStyles();
    const params = useParams();
    const history = useHistory();


    const getRequest = () => {
        setRequestLoading(true);
        api.get(`/admin/club/resignation-requests/${params.id}`)
            .then(res => {
                // console.log(res)
                setRequest(res.request);
                setUser(res.request.user);
                setMembership(res.membership);
                })
            .finally(() => setRequestLoading(false))
    }

    useEffect(() => {
        getRequest();
    }, []);


    // Poseter Functions

    const postApprove = () => {
        setRequestLoading(true);
        api.post(`/admin/club/approve-resignation`, {id: params.id})
            .then(() => {
                setVerifyApproveResignationOpen(false);
                history.push('/admin/resignation-requests')
            })
            .catch(setRequestLoading(false))
    }

    const postDeny = () => {
        setRequestLoading(true);
        api.post(`/admin/club/deny-resignation`, {
            id: params.id,
        })
            .then(getRequest())
            .finally(history.push('/admin/resignation-requests'))
    }

    // Dialog Functions

    const verifyApproveRequest = () => {
        setVerifyApproveResignationOpen(true);
    }

    const handleApproveRequestCancel = () => {
        setVerifyApproveResignationOpen(false);
    }


    // Helper Functions

    const hasHomeAddress = () => {
        if (user && user.address_one && user.city && user.state && user.zip) return true;
        return false;
    }

    const hasWorkAddress = () => {
        if (user && user.work_address_one && user.work_city && user.work_state && user.work_zip) return true;
        return false;
    }

    const hasPersonal = () => {
        if (user && (user.marital_status || user.nickname)) return true;
        return false;
    }

    const hasWork = () => {
        if (user && (user.industry || user.employer || user.position )) return true;
        return false;
    }

    const hasContact = () => {
        if (user && (
            user.email ||
            user.phone_home ||
            user.phone_mobile ||
            user.phone_work)
        ) return true;

        return false;
    }

    return (

        <>
            <div className={classes.container}>
                <div className={classes.nav}> 
                    <Typography color="textSecondary" align="center" component="div">
                        <PolicyIcon style={{ fontSize: 180 }} color="inherit" />
                        <Typography variant="h4" color="inherit"gutterBottom >
                            Resignation Request
                        </Typography>
                    </Typography>
                            <Divider />
                            <MenuList >
                                <MenuItem
                                    onClick={verifyApproveRequest}
                                    className={classes.menuItem}
                                    >
                                    <ListItemIcon>
                                        <AcceptIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Approve Request" />
                                </MenuItem>
                                <MenuItem
                                    onClick={postDeny}
                                    className={classes.menuItem}
                                    >
                                    <ListItemIcon>
                                        <CloseIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Deny Request" />
                                </MenuItem>
                                <MenuItem
                                    to="/admin/resignation-requests/"
                                    component={RouterLink}
                                    className={classes.menuItem}
                                    >
                                    <ListItemIcon>
                                        <BackIcon />
                                    </ListItemIcon>
                                    <ListItemText primary="Back To Requests" />
                                </MenuItem>
                            </MenuList>
                </div>
                <div className={classes.content}>
                        <div className={classes.skeleton}>
                            {requestLoading ? (
                                <>
                                <Skeleton animation="wave" height={30} width="30%" className={classes.paperMembership} />
                                </>
                            ) : ( request &&
                                <Grid container direction="row" alignItems="center" justify="center" spacing={2} className={classes.paperMembership}>
                                    <Grid item>
                                        <Typography variant="h5">
                                            Requested On:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="h5" color="textSecondary">
                                            {DateTime.fromSQL(request.meta.member_resigned_at).toLocaleString(DateTime.DATE_MED)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            )}
                        </div>
                    {requestLoading ? (
                        <DetailSkeleton />
                    ) : user ? (
                    <>
                    <Paper className={classes.paperMembership} >
                        <Fragment>
                            <div className={classes.sectionCenter}>
                                <Typography variant="h6" component="h3" id="user-dialogue-title">
                                    {user.full_name}
                                </Typography>
                                <Typography variant="subtitle1" component="h4" color="textSecondary">
                                    {user.public_membership_title}
                                </Typography>
                            </div>
                            <div className={classes.section}>
                                <Typography variant="subtitle1" component="h4" gutterBottom>
                                    Personal Information
                                </Typography>
                                { (hasPersonal()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        { (user.marital_status && user.marital_status != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Marital Status:</Grid>
                                                <Grid item><strong>
                                                    {user.marital_status}{user.spouse ? ' - ' + user.spouse : ""}
                                                </strong></Grid>
                                            </Grid>
                                        }
                                        { (user.nickname && user.nickname != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Nickname:</Grid>
                                                <Grid item><strong>{user.nickname}</strong></Grid>
                                            </Grid>
                                        }
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No contact information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.section}>
                                <Typography variant="body1" component="h4" gutterBottom>
                                    Contact Information
                                </Typography>
                                { (hasContact()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        { (user.email && user.email != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                    <Grid item>Email:</Grid>
                                                    <Grid item>
                                                        <strong>
                                                            <Link
                                                                color="secondary"
                                                                href={'mailto:' + user.email}
                                                                >
                                                                {user.email}
                                                            </Link>
                                                        </strong>
                                                    </Grid>
                                            </Grid>
                                        }
                                        { (user.phone_home && user.phone_home != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                    <Grid item>Home Phone:</Grid>
                                                    <Grid item>
                                                        <strong>
                                                            <Link
                                                                color="secondary"
                                                                href={'tel:' + user.phone_home}
                                                                >
                                                                {FormatPhoneNumber(user.phone_home)}
                                                            </Link>
                                                        </strong>
                                                    </Grid>
                                            </Grid>
                                        }
                                        { (user.phone_work && user.phone_work != "") &&
                                            <Grid container spacing={3} justify="space-between">
                                                    <Grid item>Work Phone:</Grid>
                                                    <Grid item>
                                                        <strong>
                                                            <Link
                                                                color="secondary"
                                                                href={'tel:' + user.phone_work}
                                                                >
                                                                {FormatPhoneNumber(user.phone_work)}
                                                            </Link>
                                                        </strong>
                                                    </Grid>
                                            </Grid>
                                        }
                                        { (user.phone_mobile && user.phone_mobile != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                    <Grid item>Mobile Phone:</Grid>
                                                    <Grid item>
                                                        <strong>
                                                            <Link
                                                                color="secondary"
                                                                href={'tel:' + user.phone_mobile}
                                                                >
                                                                {FormatPhoneNumber(user.phone_mobile)}
                                                            </Link>
                                                        </strong>
                                                    </Grid>
                                            </Grid>
                                        }
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No contact information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.section}>
                                <Typography variant="body1" component="h4" gutterBottom>
                                    Home Address
                                </Typography>
                                { (hasHomeAddress()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        <Grid container spacing={1}>
                                            <Grid xs={12} item>
                                                {user.address_one}&nbsp;&nbsp;&nbsp;
                                                {user.address_two ? user.address_two : ""}<br/>
                                                {user.city}&nbsp;&nbsp;&nbsp;
                                                {user.state}<br/>
                                                {user.zip}
                                            </Grid>
                                        </Grid>
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No contact information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.section}>
                                <Typography variant="body1" component="h4" gutterBottom>
                                    Work Information
                                </Typography>
                                { (hasWork()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        { (user.industry && user.industry != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Industry:</Grid>
                                                <Grid item><strong>
                                                    {user.industry}
                                                </strong></Grid>
                                            </Grid>
                                        }
                                        { (user.employer && user.employer != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Employer:</Grid>
                                                <Grid item><strong>{user.employer}</strong></Grid>
                                            </Grid>
                                        }
                                        { (user.position && user.position != "") &&
                                            <Grid container spacing={1} justify="space-between">
                                                <Grid item>Position:</Grid>
                                                <Grid item><strong>{user.position}</strong></Grid>
                                            </Grid>
                                        }
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No work information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                            <div className={classes.section}>
                                <Typography variant="body1" component="h4" gutterBottom>
                                    Work Address
                                </Typography>
                                {(hasWorkAddress()) ? (
                                    <Typography variant="subtitle2" component="div" color="textSecondary">
                                        <Grid container spacing={1}>
                                            <Grid xs={12} item>
                                                {user.work_address_one}&nbsp;&nbsp;&nbsp;
                                                {user.work_address_two ? user.work_address_two : ""}<br/>
                                                {user.work_city}&nbsp;&nbsp;&nbsp;
                                                {user.work_state}<br/>
                                                {user.work_zip}
                                            </Grid>
                                        </Grid>
                                    </Typography>
                                ) : (
                                    <Typography variant="body2" component="div">
                                        No work address information available. Please contact {app.club.name} for more information at <Link color="secondary" href={"mailto:" + app.club.support_email}>{app.club.support_email}</Link>.
                                    </Typography>
                                )}
                            </div>
                        </Fragment>
                    </Paper>
                    { membership &&
                    <Paper className={classes.paperMembership}>
                        <Grid container direction="column" spacing={1} justify="center">
                            <Grid item>
                                <Grid container direction="row" spacing={2} justify="center">
                                    <Grid item>
                                        <Typography variant="subtitle1">
                                            Member Since:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1" color="textSecondary">
                                            {DateTime.fromSQL(request.club_verified_at).toLocaleString(DateTime.DATE_MED)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <Grid container direction="row" spacing={2} justify="center">
                                    <Grid item>
                                        <Typography variant="subtitle1">
                                            Membership Level:
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="subtitle1" color="textSecondary">
                                            {app.user.level_id}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    {(membership && membership.in_good_standing) &&
                        <>
                            <div className={classes.membershipIconContainer}>
                                <MembershipGoodIcon className={classes.membershipGoodIcon}/>
                            </div>
                            <Typography variant="subtitle2" component="h3" align="center" gutterBottom>
                                This user's membership is in
                                <Typography variant="inherit" display="inline" className={classes.textSuccess} component="span">
                                    &nbsp;good&nbsp;
                                </Typography>
                                standing.
                            </Typography>
                            { membership.is_membership_paid ? (
                                <>
                                    <Typography variant="subtitle2" gutterBottom color="textSecondary" align="center">
                                        This user has no membership dues to pay at this time. 
                                        {/* Memberships {membership.renews_on && "renew on " + membership.renews_on + " and "}may be paid <Link color="secondary" component={RouterLink} to="/members/membership">here</Link> or by check. */}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="subtitle2" gutterBottom align="center">
                                        This user's membership is due for payment at this time. 
                                    </Typography>
                                </>
                            )}
                        </>
                    }
                    {(membership && ! membership.in_good_standing) &&
                        <>
                            <div className={classes.membershipIconContainer}>
                                <MembershipBadIcon className={classes.membershipBadIcon}/>
                            </div>
                            <Typography variant="subtitle2" color="textSecondary" component="h3" align="center" gutterBottom>
                                This user's membership is in
                                <Typography variant="inherit" color="error" display="inline" component="span">
                                    &nbsp;bad&nbsp;
                                </Typography>
                                standing.
                            </Typography>
                            <Typography variant="subtitle2" gutterBottom align="center">
                                Membership payment was due on or before
                                <Typography variant="subtitle2" color="error" display="inline" component="span" >
                                    &nbsp;{membership.due_on}&nbsp;
                                </Typography>.
                            </Typography>
                        </>
                    }
                    </Paper>
                    }
                    </>
                    ) : (
                    <Typography
                        component="h3"
                        color="textSecondary"
                        variant="h6"
                        className={classes.emptyCon}
                        >
                        No matching guests found.
                    </Typography>
                    )}
                </div>
            </div>
            <Dialog
                open={verifyApproveResignationOpen}
                onClose={handleApproveRequestCancel}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                >
                <DialogTitle id="alert-dialog-title">Confirm User Resignation</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        This action will remove any content this user has created and will disable in-app communications. Administrators will no longer be able to access their details and manage their membership. Are you sure you want to accept this resignation request?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleApproveRequestCancel} color="primary" autoFocus>
                        Cancel
                    </Button>
                    <RedButton onClick={postApprove} variant="contained">
                        Approve Resignation
                    </RedButton>
                </DialogActions>
            </Dialog>
        </>
    )
}